/**
 * Profile Popup for Poker Reborn
 *
 * Displays another player's public profile when clicking their avatar at the table.
 * Fetches fresh data from ACC via `# account public_profile <username>`.
 *
 * @see docs/screens/room/popups/PROFILE_POPUP.md - Visual specs, behavior
 * @see tasks/active/T60_PROFILE_COMMANDS_CLIENT.md - Implementation details
 */

import { Assets, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { Button, PRIMARY_BUTTON_STYLE } from '@/components/common/Button';
import { accountService, type PublicProfileData } from '@/services/AccountService';
import { networkService } from '@/services/NetworkService';
import { getAvatarPath } from '@/config/avatars';
import { formatBalance } from '@/utils/format';
import { ServiceGLIDs, Commands } from '@/types/protocol';
import type { PlayerState } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  popup: {
    width: 320,
    height: 340,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
  },

  avatar: {
    size: 64,
    borderColor: 0xd4af37,
    borderWidth: 3,
    backgroundColor: 0x333333,
  },

  displayName: {
    fill: 0xffffff,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  usernameHint: {
    fill: 0x888888,
    fontSize: 12,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontStyle: 'italic' as const,
  },

  chipsLabel: {
    fill: 0x888888,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
  },

  chipsValue: {
    fill: 0xffd700,
    fontSize: 28,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  loading: {
    fill: 0x666666,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontStyle: 'italic' as const,
  },

  error: {
    fill: 0xff4444,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
  },
};

// =============================================================================
// PROFILE POPUP
// =============================================================================

export class ProfilePopup extends Container {
  private overlay: Graphics;
  private popupContainer: Container;
  private background: Graphics;
  private avatarContainer: Container;
  private displayNameText: Text;
  private usernameHintText: Text;
  private chipsLabelText: Text;
  private chipsValueText: Text;
  private statusText: Text;
  private closeButton: Button;

  private screenWidth: number;
  private screenHeight: number;

  /** Callback when player dismisses */
  onDismiss: (() => void) | null = null;

  constructor(screenWidth: number, screenHeight: number, player: PlayerState) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create overlay (click to dismiss)
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', () => { this.handleDismiss(); });
    this.addChild(this.overlay);

    // Create popup container (centered)
    this.popupContainer = new Container();
    this.popupContainer.x = screenWidth / 2;
    this.popupContainer.y = screenHeight / 2;
    this.addChild(this.popupContainer);

    // Create background
    this.background = new Graphics();
    this.popupContainer.addChild(this.background);
    this.drawBackground();

    // Block popup clicks from dismissing
    this.background.eventMode = 'static';
    this.background.on('pointerdown', (e) => { e.stopPropagation(); });

    const halfHeight = STYLES.popup.height / 2;

    // Avatar display
    this.avatarContainer = new Container();
    this.avatarContainer.y = -halfHeight + 60;
    this.popupContainer.addChild(this.avatarContainer);
    this.drawAvatarPlaceholder(player.profile.name);

    // Display name
    this.displayNameText = new Text({ text: player.profile.name, style: STYLES.displayName });
    this.displayNameText.anchor.set(0.5);
    this.displayNameText.y = -halfHeight + 110;
    this.popupContainer.addChild(this.displayNameText);

    // Username hint (shown when nickname differs from username)
    this.usernameHintText = new Text({ text: '', style: STYLES.usernameHint });
    this.usernameHintText.anchor.set(0.5);
    this.usernameHintText.y = -halfHeight + 132;
    this.usernameHintText.visible = false;
    this.popupContainer.addChild(this.usernameHintText);

    // Chips label
    this.chipsLabelText = new Text({ text: 'Balance', style: STYLES.chipsLabel });
    this.chipsLabelText.anchor.set(0.5);
    this.chipsLabelText.y = 10;
    this.chipsLabelText.visible = false;
    this.popupContainer.addChild(this.chipsLabelText);

    // Chips value
    this.chipsValueText = new Text({ text: '', style: STYLES.chipsValue });
    this.chipsValueText.anchor.set(0.5);
    this.chipsValueText.y = 38;
    this.chipsValueText.visible = false;
    this.popupContainer.addChild(this.chipsValueText);

    // Status text (loading / error)
    this.statusText = new Text({ text: 'Loading...', style: STYLES.loading });
    this.statusText.anchor.set(0.5);
    this.statusText.y = 20;
    this.popupContainer.addChild(this.statusText);

    // Close button
    const closeBtnWidth = 100;
    const closeBtnHeight = 36;
    this.closeButton = new Button({
      label: 'Close',
      width: closeBtnWidth,
      height: closeBtnHeight,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.closeButton.onClick = () => { this.handleDismiss(); };
    this.closeButton.x = -closeBtnWidth / 2;
    this.closeButton.y = halfHeight - 48;
    this.popupContainer.addChild(this.closeButton);

    // Listen for server response and fetch
    accountService.on('publicProfile', this.onPublicProfile);
    this.fetchProfile(player.profile.id);
  }

  /** Resize popup for screen changes */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    this.popupContainer.x = width / 2;
    this.popupContainer.y = height / 2;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    const { width, height, fill, stroke, cornerRadius } = STYLES.popup;

    this.background.clear();
    this.background.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    this.background.fill(fill);
    this.background.setStrokeStyle({ width: 2, color: stroke });
    this.background.stroke();
  }

  private fetchProfile(username: string): void {
    networkService.send(ServiceGLIDs.ACCOUNT, Commands.fetchPublicProfile(username));
  }

  private onPublicProfile = (data: PublicProfileData): void => {
    if (this.destroyed) return;

    if (data.error) {
      this.statusText.text = 'Player not found';
      this.statusText.style.fill = STYLES.error.fill;
      return;
    }

    // Hide loading
    this.statusText.visible = false;

    // Update display name: prefer nickname, fallback to username
    const nickname = typeof data.profile.nickname === 'string' ? data.profile.nickname : null;
    const displayName = nickname ?? data.username;
    this.displayNameText.text = displayName;

    // Show @username hint if nickname is set
    if (nickname) {
      this.usernameHintText.text = `@${data.username}`;
      this.usernameHintText.visible = true;
    }

    // Show chips
    this.chipsLabelText.visible = true;
    this.chipsValueText.text = formatBalance(data.chips);
    this.chipsValueText.visible = true;

    // Load avatar if available
    const avatarId = typeof data.profile.avatar === 'number' ? data.profile.avatar : null;
    if (avatarId !== null) {
      this.loadAvatar(avatarId);
    }
  };

  private drawAvatarPlaceholder(name: string): void {
    const { size, borderColor, borderWidth, backgroundColor } = STYLES.avatar;
    const radius = size / 2;

    // Gold border circle
    const border = new Graphics();
    border.circle(0, 0, radius);
    border.fill(borderColor);
    this.avatarContainer.addChild(border);

    // Inner background circle
    const bg = new Graphics();
    bg.circle(0, 0, radius - borderWidth);
    bg.fill(backgroundColor);
    this.avatarContainer.addChild(bg);

    // First letter placeholder
    const firstLetter = name.charAt(0).toUpperCase() || '?';
    const placeholder = new Text({
      text: firstLetter,
      style: {
        fill: 0xffffff,
        fontSize: 28,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    placeholder.anchor.set(0.5);
    this.avatarContainer.addChild(placeholder);
  }

  private loadAvatar(avatarId: number): void {
    const { size, borderWidth } = STYLES.avatar;
    const radius = size / 2;
    const path = getAvatarPath(avatarId);

    void Assets.load<Texture>(path).then((texture) => {
      if (this.destroyed) return;

      // Clear placeholder and redraw border + bg
      this.avatarContainer.removeChildren();

      const border = new Graphics();
      border.circle(0, 0, radius);
      border.fill(STYLES.avatar.borderColor);
      this.avatarContainer.addChild(border);

      const bg = new Graphics();
      bg.circle(0, 0, radius - borderWidth);
      bg.fill(STYLES.avatar.backgroundColor);
      this.avatarContainer.addChild(bg);

      const sprite = new Sprite(texture);
      sprite.width = size - borderWidth * 2;
      sprite.height = size - borderWidth * 2;
      sprite.anchor.set(0.5);

      const mask = new Graphics();
      mask.circle(0, 0, radius - borderWidth);
      mask.fill(0xffffff);
      this.avatarContainer.addChild(mask);

      sprite.mask = mask;
      this.avatarContainer.addChild(sprite);
    });
  }

  private handleDismiss(): void {
    this.onDismiss?.();
    this.destroy();
  }

  override destroy(): void {
    accountService.off('publicProfile', this.onPublicProfile);
    super.destroy({ children: true });
  }
}
